import React from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  AlertTriangle,
  Lightbulb,
  type LucideIcon,
  Scale,
  Sparkles,
} from "lucide-react";
import type {
  CommunityGovernance,
  CommunityIssueActivityRow,
} from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import EntityAvatar from "components/Entities/EntityAvatar";
import SectionShell from "../SectionShell";
import {
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import {
  buildIssueHref,
  getIssueCoverageState,
  resolveCommunityTeabates,
} from "../utils";

interface OpenOpportunitiesProps {
  issueActivityQuery: UseQueryResult<CommunityIssueActivityRow[]>;
  governanceQuery: UseQueryResult<CommunityGovernance>;
  labels: CommunityUiStrings;
}

interface GovernanceOpportunity {
  key: string;
  title: string;
  body: string;
  cta: string;
  href: string;
  icon: LucideIcon;
}

function formatCountTemplate(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

/**
 * Strategy and governance opportunities derived from the dashboard aggregates.
 */
const OpenOpportunities: React.FC<OpenOpportunitiesProps> = ({
  issueActivityQuery,
  governanceQuery,
  labels,
}) => {
  const isPending = issueActivityQuery.isPending || governanceQuery.isPending;
  const isError = issueActivityQuery.isError || governanceQuery.isError;
  const isRetrying = issueActivityQuery.isRefetching || governanceQuery.isRefetching;

  return (
    <SectionShell
      title={labels.openOpportunitiesTitle}
      subtitle={labels.openOpportunitiesSubtitle}
      contentClassName="space-y-4"
    >
      {isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void Promise.all([issueActivityQuery.refetch(), governanceQuery.refetch()]);
          }}
          isRetrying={isRetrying}
        />
      ) : (
        (() => {
          const issueRows = issueActivityQuery.data || [];
          const governance = governanceQuery.data;
          const strategyGaps = issueRows
            .filter((row) => getIssueCoverageState(row) !== "complete")
            .slice(0, 4);
          const teabates = resolveCommunityTeabates(governance);
          const governanceGaps: GovernanceOpportunity[] = governance
            ? [
                governance.feedback.total_open > 0
                  ? {
                      key: "feedback",
                      title: labels.feedbackPipelineTitle,
                      body: formatCountTemplate(
                        labels.opportunityFeedbackQueue,
                        governance.feedback.total_open
                      ),
                      cta: labels.opportunityReviewFeedback,
                      href: "/user-feedback",
                      icon: Lightbulb,
                    }
                  : null,
                teabates.total_active_teabates > teabates.alignment_participants
                  ? {
                      key: "teabates",
                      title: labels.teabateParticipationTitle,
                      body: formatCountTemplate(
                        labels.opportunityTeabateParticipation,
                        teabates.total_active_teabates
                      ),
                      cta: labels.opportunityJoinTeabates,
                      href: "/teabates/top",
                      icon: Scale,
                    }
                  : null,
                governance.sentiment.participants < governance.sentiment.total_responses
                  || governance.sentiment.participants < Math.max(1, governance.reports.total_in_window)
                  ? {
                      key: "sentiment",
                      title: labels.communitySentimentTitle,
                      body: labels.opportunitySentimentCoverage,
                      cta: labels.opportunityRecordSentiment,
                      href: "/issues/top",
                      icon: Sparkles,
                    }
                  : null,
              ].filter((gap): gap is GovernanceOpportunity => gap !== null)
            : [];

          return (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div className="space-y-3 rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {labels.opportunityStrategyTitle}
                  </p>
                </div>
                {strategyGaps.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-eoe-border/70 px-4 py-6 text-sm text-eoe-text-sub dark:border-eoe-dark-border/70 dark:text-eoe-dark-text-sub">
                    {labels.opportunityNoStrategyGaps}
                  </div>
                ) : (
                  strategyGaps.map((row) => {
                    const state = getIssueCoverageState(row);
                    const description =
                      state === "missing_both"
                        ? labels.opportunityMissingBoth
                        : state === "missing_solution"
                          ? labels.opportunityMissingSolution
                          : labels.opportunityMissingAction;
                    const cta =
                      state === "missing_solution" || state === "missing_both"
                        ? labels.opportunityProposeSolution
                        : labels.opportunityStartAction;

                    return (
                      <div
                        key={row.issue_id}
                        className="rounded-2xl border border-eoe-border/70 bg-white/60 p-4 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30"
                      >
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <AlertTriangle
                              className="mt-1 size-4 shrink-0 text-amber-600 dark:text-amber-400"
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1 space-y-2">
                              <EntityAvatar
                                entityId={row.issue_id}
                                entityType="issue"
                                showLink
                                isInputField={false}
                                hasRing={false}
                              />
                              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                {description}
                              </p>
                            </div>
                          </div>
                          <Link
                            to={buildIssueHref(row.issue_cname, row.issue_name)}
                            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {cta}
                          </Link>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {labels.opportunityGovernanceTitle}
                  </p>
                </div>
                {governanceGaps.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-eoe-border/70 px-4 py-6 text-sm text-eoe-text-sub dark:border-eoe-dark-border/70 dark:text-eoe-dark-text-sub">
                    {labels.opportunityNoGovernanceGaps}
                  </div>
                ) : (
                  governanceGaps.map((gap) => {
                    const Icon = gap.icon;

                    return (
                      <div
                        key={gap.key}
                        className="rounded-2xl border border-eoe-border/70 bg-white/60 p-4 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30"
                      >
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <Icon className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
                            <div className="space-y-1">
                              <p className="font-medium text-eoe-text dark:text-eoe-dark-text">{gap.title}</p>
                              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{gap.body}</p>
                            </div>
                          </div>
                          <Link
                            to={gap.href}
                            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {gap.cta}
                          </Link>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()
      )}
    </SectionShell>
  );
};

export default OpenOpportunities;

